import { useState, useRef, useEffect } from 'react';
import { IonIcon, useIonRouter } from '@ionic/react';
import { logOutOutline } from 'ionicons/icons';
import { useAuthStore } from '../../store/useAuthStore';
import { useProfileStore } from '../../store/useProfileStore';
import { logout } from '../../services/authService';
import { Breadcrumb } from '../shared/Breadcrumb';
import { ChangePasswordModal } from './ChangePasswordModal';
import hamIcon from '../../assets/ham-icon.svg';

interface DesktopHeaderProps {
  onToggleSidebar: () => void;
  isXL: boolean;
}

export function DesktopHeader({ onToggleSidebar, isXL }: DesktopHeaderProps) {
  const router = useIonRouter();
  const { isAuthenticated } = useAuthStore();
  const { activeProfile } = useProfileStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    await logout();
    router.push('/sign-in', 'none');
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-12 px-4 sm:px-6 lg:px-8">
        {/* Hamburger Menu + Breadcrumb */}
        <div className="flex items-center gap-4">
          {!isXL && isAuthenticated && (
            <button
              onClick={onToggleSidebar}
              className="flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded transition-colors"
              aria-label="Toggle menu"
            >
              <IonIcon icon={hamIcon} className="text-xl" color="medium" />
            </button>
          )}
          <Breadcrumb />
        </div>

        {/* Center spacer */}
        <div className="flex-1" />

        {/* Navigation & Auth */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4">
            {!isAuthenticated ? (
              <button
                onClick={() => router.push('/sign-in', 'none')}
                className="text-gray-700 hover:text-gray-900 transition-colors"
              >
                Sign In
              </button>
            ) : (
              <div className="flex items-center gap-3">
                {activeProfile && (
                  <div ref={menuRef} className="relative">
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
                    >
                      {activeProfile.firstName} {activeProfile.lastName}
                    </button>
                    {userMenuOpen && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[160px]">
                        <button
                          onClick={() => { setUserMenuOpen(false); setShowChangePassword(true); }}
                          className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Change Password
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm"
                  aria-label="Sign out"
                >
                  <IonIcon icon={logOutOutline} className="text-lg" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showChangePassword && activeProfile && (
        <ChangePasswordModal
          email={activeProfile.email}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </header>
  );
}
